import React, { useEffect, useState, useMemo, Fragment } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { createStructuredSelector } from 'reselect';

import YearSelect from '@common-components/yearSelect/YearSelect';
import ApplicationPanel from '@components/individualPortal/applications/ApplicationPanel';
import AlertMessage from '@components/individualPortal/dashboard/AlertMsgs/AlertMessage';
import { HistoricalApplicationView, NoApplicationsFound } from '@modules/historical-application';
import HistoricalAccountMigrationSection from '@components/individualPortal/applications/HistoricalAccountMigrationSection';

import { getApplications, getHistoricalApplications } from '@actions/individualPortal/applications/applicationsActionCreators';
import { getEnvironment, showModal } from '@actions/common/actionCreators';
import { cancelApp } from '@actions/individualPortal/dashboard/dashboardActionCreators';
import { loadedSelector, sortedApplicationsSelector, householdCaseIdSelector, historicalApplicationsSelector, historicalMigrationSelector } from '@selectors/applicationsSelectors';
import { environmentSelector } from '@selectors/commonSelectors';
import { dashboardModalTypes } from '@constants/individualPortal/dashboard/modals/dashboardModalTypes';
import { configs } from '@constants/configs/configs';
import { getYearDropDownOptions, isHistoricalYear } from '@modules/historical-application/utils/ApplicationContainerUtils';
import { ConditionalRendering } from '@components/common/conditional-rendering';


const ApplicationContainer = (props) => {

    const {
        environment: {
            stateCode,
            isState,
            isMNFullStack,
            isStateFullStack,
            coverageYear,
            exchangeStartYear,
            exchangeName,
            language,
            stateSubsidyEnabled,
            localDates: {
                serverDate,
                oepStartDate
            },
            flags:{
                isQleMultipleDocumentSupport,
                showHistoricalData,
                isHistoricalRefIdLinkingEnabled
            }
        },
        loaded,
        applications,
        historicalApplications,
        historicalMigration,
        householdCaseId,
        getApplications,
        getHistoricalApplications,
        getEnvironment,
        cancelApp,
        showModal
    } = props;

    const yearsDropDownOptions = useMemo(
        () => getYearDropDownOptions(serverDate, oepStartDate, coverageYear, exchangeStartYear, showHistoricalData),
        [exchangeStartYear, coverageYear, showHistoricalData, serverDate, oepStartDate]
    );

    const lastYear = useMemo(
        () => {
            const lastYearFromOptions = [ ...yearsDropDownOptions ].pop();
            return lastYearFromOptions ? lastYearFromOptions : exchangeStartYear;
        },
        [ yearsDropDownOptions ]
    );

    const [selectedYear, setSelectedYear] = useState(lastYear);

    useEffect(() => {
        getEnvironment();
    }, []);

    const isSelectedYearHistorical = useMemo(
        () => isHistoricalYear(selectedYear, exchangeStartYear),
        [selectedYear, exchangeStartYear]
    );

    useEffect(() => {
        if (isSelectedYearHistorical) {
            getHistoricalApplications(selectedYear);
        } else {
            getApplications(selectedYear);
        }
    }, [selectedYear, isSelectedYearHistorical]);

    const latestApp = (applications && applications.currentApps[0] && applications.currentApps[0].caseNumber) ||
        (applications && applications.pastApps[0] && applications.pastApps[0].caseNumber);


    const getSectionHeader = (sectionName, year) => {
        const CURRENT_APPS = 'currentApps';
        const PAST_APPS = 'pastApps';

        switch (sectionName) {
            case CURRENT_APPS:
                return <FormattedMessage id='applications.current_app' values={{ year }}/>;
            case PAST_APPS:
                return <FormattedMessage id='applications.past_app' values={{ year }}/>;
        }
    };

    const historicalApp = selectedYear !== lastYear;

    const onCancelApp = (caseNumber) => {
        showModal({
            modalType: dashboardModalTypes.CANCEL_APP_MODAL,
            modalData: { cb:  cancelApp.bind(null, configs.endpoints.CANCEL_APP, { caseNumber })
            }
        });
    };

    const renderHistoricalView = () => {
        if (historicalApplications && historicalApplications.length > 0) {
            return (
                <div className='application__section'>
                    {historicalApplications.map(application => (
                        <HistoricalApplicationView 
                            key={application.applicationId}
                            data={application}
                            selectedYear={selectedYear}
                        />
                    ))}
                </div>
            );
        }

        if (isHistoricalRefIdLinkingEnabled && historicalMigration?.migrated === false) {
            return (
                <HistoricalAccountMigrationSection />
            );
        }

        return <NoApplicationsFound year={selectedYear} />;
    };

    const renderRegularView = () => (
        applications ?
            <Fragment>
                <ConditionalRendering isTrue={historicalApp} >
                    <AlertMessage
                        content={<FormattedMessage id='applications.alert' values={{ currentYear: lastYear }}/>}
                        type='warning'/>
                </ConditionalRendering>
                {
                    Object.keys(applications).map(section => {
                        if (applications[section].length > 0) {
                            return (
                                <div className="application__section" key={section}>
                                    <h2 className="application__section__header">
                                        {getSectionHeader(section, selectedYear)}
                                    </h2>

                                    {applications[section].map(application => (
                                        <ApplicationPanel
                                            key={application.caseNumber}
                                            latestApp={latestApp === application.caseNumber}
                                            appPanelData={{ ...application, householdCaseId }}
                                            envData={{
                                                stateCode,
                                                isState,
                                                isMNFullStack,
                                                isStateFullStack,
                                                language,
                                                stateSubsidyEnabled,
                                                isQleMultipleDocumentSupport,
                                            }}
                                            history={history}
                                            onCancelApp={onCancelApp}
                                        />
                                    ))}
                                </div>
                            );
                        }

                    })
                }
            </Fragment> :
            <p className='gi-panel gi-application-panel application'>
                <FormattedMessage id='applications.no_app' values={{ exchangeName }}/>
            </p>
    );

    return (
        loaded ?
            <div id="applications" className="usa-layout-docs gi-main-section applications bg-transparent">
                <YearSelect
                    msgId="applications.year_dropdown.heading"
                    years={yearsDropDownOptions}
                    initialYear={lastYear}
                    onChangeHandler={(selectedYear) => setSelectedYear(selectedYear)}
                />
                {isSelectedYearHistorical ? renderHistoricalView() : renderRegularView()}
            </div> : null
    );
};

const mapStateToProps = createStructuredSelector({
    environment: environmentSelector,
    loaded: loadedSelector,
    householdCaseId: householdCaseIdSelector,
    applications: sortedApplicationsSelector,
    historicalApplications: historicalApplicationsSelector,
    historicalMigration: historicalMigrationSelector
});

export default connect(mapStateToProps, { getApplications, getHistoricalApplications, getEnvironment, cancelApp, showModal })(ApplicationContainer);
